// One answer from the hospital's approved clinical references (G1): each
// statement with its numbered sources, the sources themselves (document,
// version, section, page and the exact passage), or a plain "the references
// don't cover this". Shared by the 📚 References drawer and ClintoBytes
// Copilot, so a doctor sees the same thing wherever they asked.
import { useRef, useState } from 'react';
import { BookOpen, ChevronDown, ChevronRight, ClipboardCopy, FileInput, Info, ThumbsDown, ThumbsUp, UserRound } from 'lucide-react';
import { cn } from '@/lib/cn';
import { toast } from '@/hooks/useToast';
import {
  recordReferenceAction, statementWithSources,
  type ReferenceAction, type ReferenceAnswer, type ReferenceSource, type SectionKey,
} from './api';

const ORDER: SectionKey[] = ['key_points', 'possible_conditions', 'treatment_approaches', 'medicine_info', 'diet_lifestyle'];

function sourceLine(s: ReferenceSource) {
  const section = s.heading && s.heading !== s.title ? s.heading.replace(`${s.title} › `, '') : null;
  return [s.version_label ? `v${s.version_label}` : null, section, s.page ? `page ${s.page}` : null].filter(Boolean).join(' · ');
}

export function ReferenceAnswerView({
  answer, compact = false, onInsert,
}: {
  answer: ReferenceAnswer;
  /** Copilot shows it inside a chat bubble: smaller type, sources collapsed. */
  compact?: boolean;
  /** Present when a Consultation Notes draft is open to receive the text. */
  onInsert?: (text: string) => void;
}) {
  const [open, setOpen] = useState<Set<number>>(() => new Set(compact ? [] : answer.sources.slice(0, 1).map((s) => s.n)));
  const [rated, setRated] = useState<ReferenceAction | null>(['helpful', 'not_helpful'].includes(answer.doctor_action) ? answer.doctor_action as ReferenceAction : null);
  const [showContext, setShowContext] = useState(false);
  const sourceRefs = useRef<Record<number, HTMLLIElement | null>>({});
  const text = compact ? 'text-[12.5px]' : 'text-[13px]';

  const act = (action: ReferenceAction) => recordReferenceAction(answer.query_id, action).catch(() => { /* a lost rating is not worth an error */ });
  const showSource = (n: number) => {
    setOpen((o) => new Set(o).add(n));
    requestAnimationFrame(() => sourceRefs.current[n]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' }));
  };
  const copy = async (t: string) => {
    try { await navigator.clipboard.writeText(t); toast({ title: 'Copied with its source', variant: 'success' }); act('copied'); }
    catch { toast({ title: 'Could not copy', description: 'Select the text and copy it instead.', variant: 'danger' }); }
  };

  const ctx = answer.patient_context;
  const sections = ORDER.filter((k) => answer.sections[k]?.length);

  return (
    <div className={cn('flex flex-col gap-3', text)}>
      {ctx?.status === 'sent' && (
        <div className="rounded-lg border border-teal/30 bg-teal-light px-3 py-2 text-ink">
          <div className="flex items-start gap-1.5">
            <UserRound className="mt-0.5 h-3.5 w-3.5 shrink-0 text-teal" />
            <div className="min-w-0">
              <div>{ctx.note}</div>
              {ctx.text && (
                <button type="button" className="mt-0.5 inline-flex items-center gap-0.5 text-[11.5px] font-medium text-teal hover:underline" onClick={() => setShowContext((v) => !v)}>
                  {showContext ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />} What was sent
                </button>
              )}
              {showContext && ctx.text && <pre className="mt-1 whitespace-pre-wrap font-sans text-[11.5px] text-ink-muted">{ctx.text}</pre>}
            </div>
          </div>
        </div>
      )}
      {ctx?.status === 'withheld_no_consent' && (
        <div className="flex items-start gap-1.5 rounded-lg bg-warn-soft px-3 py-2 text-warn-ink">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {ctx.note}
        </div>
      )}

      {answer.insufficient_evidence ? (
        <div className="rounded-lg border border-border bg-muted px-3 py-2.5">
          <div className="font-semibold text-ink">The hospital&rsquo;s references don&rsquo;t cover this</div>
          {answer.reason && <div className="mt-0.5 text-ink-muted">{answer.reason}</div>}
          <div className="mt-1 text-[11.5px] text-ink-muted">Use your own judgement and the usual references. If a document is missing, tell the person who manages the Reference Library.</div>
        </div>
      ) : (
        <>
          {!answer.ai_used && answer.reason && <div className="rounded-lg bg-muted px-3 py-2 text-ink-muted">{answer.reason}</div>}
          {sections.map((key) => (
            <div key={key}>
              <div className={cn('mb-1 font-semibold text-ink', key === 'medicine_info' && 'flex items-center gap-1')}>
                {answer.section_labels[key]}
              </div>
              <ul className="flex list-none flex-col gap-1.5 pl-0">
                {answer.sections[key]!.map((s, i) => {
                  const withSrc = statementWithSources(s, answer.sources);
                  return (
                    <li key={i} className="group rounded-md px-1.5 py-1 hover:bg-muted">
                      <span className="text-ink">{s.text}</span>{' '}
                      {s.citations.map((n) => (
                        <button key={n} type="button" onClick={() => showSource(n)} aria-label={`Show source ${n}`}
                          className="mx-0.5 inline-flex h-[18px] min-w-[18px] items-center justify-center rounded bg-teal-light px-1 align-[1px] text-[10.5px] font-semibold text-teal hover:bg-teal hover:text-white">
                          {n}
                        </button>
                      ))}
                      <span className="ml-1 inline-flex gap-2 opacity-70 group-hover:opacity-100">
                        <button type="button" className="inline-flex items-center gap-0.5 text-[11px] text-ink-muted hover:text-teal" onClick={() => copy(withSrc)}>
                          <ClipboardCopy className="h-3 w-3" /> Copy
                        </button>
                        {onInsert && (
                          <button type="button" className="inline-flex items-center gap-0.5 text-[11px] text-ink-muted hover:text-teal"
                            onClick={() => { onInsert(withSrc); act('inserted'); }}>
                            <FileInput className="h-3 w-3" /> Insert into note
                          </button>
                        )}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
          {answer.reason && answer.ai_used && (
            <div className="rounded-md bg-warn-soft px-3 py-1.5 text-[12px] text-warn-ink">Not covered: {answer.reason}</div>
          )}
          {answer.removed_unsupported > 0 && (
            <div className="text-[11.5px] text-ink-muted">
              {answer.removed_unsupported} statement{answer.removed_unsupported === 1 ? ' was' : 's were'} left out because {answer.removed_unsupported === 1 ? 'it' : 'they'} could not be tied to a reference.
            </div>
          )}
          {!!answer.sections.medicine_info?.length && (
            <div className="text-[11.5px] text-ink-muted">Medicine information is for reference only. Nothing here is added to a prescription.</div>
          )}
        </>
      )}

      {answer.sources.length > 0 && (
        <div>
          <div className="mb-1 flex items-center gap-1 font-semibold text-ink"><BookOpen className="h-3.5 w-3.5" /> Sources</div>
          <ol className="flex list-none flex-col gap-1 pl-0">
            {answer.sources.map((s) => {
              const isOpen = open.has(s.n);
              return (
                <li key={s.n} ref={(el) => { sourceRefs.current[s.n] = el; }} className="rounded-md border border-border bg-card">
                  <button type="button" className="flex w-full items-start gap-2 px-2.5 py-1.5 text-left"
                    onClick={() => setOpen((o) => { const n = new Set(o); if (n.has(s.n)) n.delete(s.n); else n.add(s.n); return n; })}>
                    <span className="mt-[1px] inline-flex h-[18px] min-w-[18px] shrink-0 items-center justify-center rounded bg-teal-light px-1 text-[10.5px] font-semibold text-teal">{s.n}</span>
                    <span className="min-w-0 flex-1">
                      <span className="block font-medium text-ink">{s.title}</span>
                      {sourceLine(s) && <span className="block text-[11.5px] text-ink-muted">{sourceLine(s)}</span>}
                    </span>
                    {isOpen ? <ChevronDown className="mt-0.5 h-3.5 w-3.5 shrink-0 text-ink-muted" /> : <ChevronRight className="mt-0.5 h-3.5 w-3.5 shrink-0 text-ink-muted" />}
                  </button>
                  {isOpen && <blockquote className="whitespace-pre-wrap border-t border-border px-3 py-2 text-[12px] leading-relaxed text-ink">{s.excerpt}</blockquote>}
                </li>
              );
            })}
          </ol>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2 text-[11px] text-ink-muted">
        <span>
          From approved hospital references · {answer.search_mode === 'hybrid' ? 'searched by meaning and words' : 'searched by words'}
        </span>
        <span className="flex items-center gap-1">
          {(['helpful', 'not_helpful'] as const).map((a) => (
            <button key={a} type="button" aria-label={a === 'helpful' ? 'Helpful' : 'Not helpful'} aria-pressed={rated === a}
              className={cn('rounded p-1 hover:bg-muted', rated === a && 'text-teal')}
              onClick={() => { setRated(a); act(a); }}>
              {a === 'helpful' ? <ThumbsUp className="h-3.5 w-3.5" /> : <ThumbsDown className="h-3.5 w-3.5" />}
            </button>
          ))}
        </span>
      </div>
    </div>
  );
}
